import { User as OidcUser, UserManager, UserManagerSettings, WebStorageStateStore } from "oidc-client-ts"
import StorageService from "services/StorageService"
import config from "config/config"
import { StorageKey } from "config/constants"
import Routes from "@/routes"

class AuthService {
    #userManager: UserManager

    constructor() {
        // if (import.meta.env.NODE_ENV !== "production" && import.meta.env.VITEST_POOL_ID === undefined) {
        //     Log.logger = console
        //     Log.level = Log.DEBUG
        // }

        const settings: UserManagerSettings = {
            authority: config.auth.server,
            client_id: config.auth.clientId,
            redirect_uri: `${config.clientRoot}${config.auth.redirectURI}`,
            silent_redirect_uri: config.auth.silentRenewURI ? `${config.clientRoot}${config.auth.silentRenewURI}` : undefined,
            automaticSilentRenew: config.auth.silentRenewURI ? true : false,
            scope: config.auth.scope,
            userStore: new WebStorageStateStore({ store: window.sessionStorage }),
            // oidc-client-ts defaults loadUserInfo to false (oidc-client 1.x
            // defaulted to true). The IdP returns given_name/family_name/name
            // only from the UserInfo endpoint, so request it to populate the
            // profile that drives the user name + avatar initials.
            loadUserInfo: true,
        }

        this.#userManager = new UserManager(settings)

        this.#userManager.clearStaleState()
        StorageService.removeItem(StorageKey.signinRedirectURL)

        this.#userManager.events.addSilentRenewError(e => {
            console.log("Silent renew error", e)
            this.login()
        })

    }

    async getAccessToken(): Promise<string | null> {
        const user = await this.getOidcUser()
        if (user === null) {
            return null
        }
        return user.access_token
    }

    async getOidcUser(): Promise<OidcUser | null> {
        const user = await this.#userManager.getUser()
        if (!user) {
            this.login()
            return null
        }
        return user
    }

    login(): Promise<void> {
        if (window.location.pathname === Routes.noCompany.getPath()) {
            StorageService.removeItem(StorageKey.signinRedirectURL)
        } else {
            StorageService.setItem(StorageKey.signinRedirectURL, window.location.href)
        }
        return this.#userManager.signinRedirect()
    }

    requestAccess() {
        if (config.requestAccessURI) {
            StorageService.setItem(StorageKey.signinRedirectURL, window.location.href)
            window.location.href = config.requestAccessURI
        }
    }

    async renewToken(): Promise<string> {
        const user = await this.#userManager.signinSilent()
        if (!user) {
            throw new Error("Silent sign-in returned no user")
        }
        return user.access_token
    }

    logout(): Promise<void> {
        return this.#userManager.signoutRedirect()
    }
}

export default new AuthService()
